import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Autocomplete,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/de';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  useCreateProject,
  useUpdateProject,
  useProjectTypes,
} from '../../../api/hooks/useProjects';
import { useCorporations } from '../../../api/hooks/useCorporations';
import { useCorporationCostCenters } from '../../../api/hooks/useCostCenters';
import { useNotifications } from '../../../context/NotificationContext';
import { ApiError, apiClient } from '../../../api/client';
import { AppDialogTitle } from '../../../components/common/AppDialogTitle';

interface Corp {
  id: string;
  name: string;
}

interface CostCenter {
  id: string;
  name: string;
}

interface ProjectFormData {
  name: string;
  nickname: string;
  projektleitung: string;
  description: string;
  customer_id: string;
  customer_cost_centre_id: string;
  project_type_id: string;
  priority: string;
  state: string;
  bill_type: string;
  planned_start: Dayjs | null;
  planned_end: Dayjs | null;
  deadline: Dayjs | null;
  project_no: string;
  project_reference: string;
  link_uri: string;
}

interface ProjectFormDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  initialData?: Record<string, unknown>;
  onClose: () => void;
}

const INITIAL_FORM: ProjectFormData = {
  name: '',
  nickname: '',
  projektleitung: '',
  description: '',
  customer_id: '',
  customer_cost_centre_id: '',
  project_type_id: '',
  priority: 'NORMAL',
  state: 'PLANNED',
  bill_type: 'BILLABLE',
  planned_start: null,
  planned_end: null,
  deadline: null,
  project_no: '',
  project_reference: '',
  link_uri: '',
};

const STATES = ['PLANNED', 'STARTED', 'IN_PROGRESS', 'COMPLETED', 'CANCELED'];
const PRIORITIES = ['LOW', 'NORMAL', 'HIGH', 'URGENT'];

export function ProjectFormDialog({ open, mode, initialData, onClose }: ProjectFormDialogProps) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');
  const { notify } = useNotifications();
  const navigate = useNavigate();

  const createMutation = useCreateProject();
  const updateMutation = useUpdateProject();
  const { data: typesData } = useProjectTypes();
  const projectTypes = (typesData as unknown as { data?: Array<{ id: string; name: string }> } | undefined)?.data ?? [];

  const { data: corpsData } = useCorporations({ limit: 100, classification: 'customer' });
  const customers = useMemo(() => {
    const resp = corpsData as unknown as { data?: Corp[] } | undefined;
    return resp?.data ?? [];
  }, [corpsData]);

  const [form, setForm] = useState<ProjectFormData>(INITIAL_FORM);

  const { data: ccData } = useCorporationCostCenters(form.customer_id || undefined);
  const costCenters = useMemo(() => {
    const resp = ccData as unknown as { data?: CostCenter[] } | undefined;
    return resp?.data ?? [];
  }, [ccData]);

  useEffect(() => {
    if (open && mode === 'edit' && initialData) {
      setForm({
        name: (initialData.name as string) ?? '',
        nickname: (initialData.nickname as string) ?? '',
        projektleitung: '',
        description: (initialData.description as string) ?? '',
        customer_id: (initialData.customer_id as string) ?? '',
        customer_cost_centre_id: (initialData.customer_cost_centre_id as string) ?? '',
        project_type_id: (initialData.project_type_id as string) ?? '',
        priority: (initialData.priority as string) ?? 'NORMAL',
        state: (initialData.state as string) ?? 'PLANNED',
        bill_type: (initialData.bill_type as string) ?? 'BILLABLE',
        planned_start: initialData.planned_start ? dayjs(initialData.planned_start as string) : null,
        planned_end: initialData.planned_end ? dayjs(initialData.planned_end as string) : null,
        deadline: initialData.deadline ? dayjs(initialData.deadline as string) : null,
        project_no: (initialData.project_no as string) ?? '',
        project_reference: (initialData.project_reference as string) ?? '',
        link_uri: (initialData.link_uri as string) ?? '',
      });
    } else if (open && mode === 'create') {
      setForm(INITIAL_FORM);
    }
  }, [open, mode, initialData]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      notify(tc('errors.requiredFields', 'Pflichtfelder ausfüllen'), 'warning');
      return;
    }

    if (mode === 'create' && !form.projektleitung.trim()) {
      notify(t('participants.leadRequired', 'Projektleitung ist ein Pflichtfeld'), 'warning');
      return;
    }

    // Start- und Enddatum sind Pflicht fuer in diesem Tool angelegte Projekte
    // (rein Frontend-seitig — SMS PRO darf weiterhin ohne anlegen).
    if (mode === 'create' && (!form.planned_start || !form.planned_end)) {
      notify(t('project.datesRequired', 'Geplanter Start und geplantes Ende sind Pflichtfelder'), 'warning');
      return;
    }

    // Reihenfolge prüfen, sobald beide Daten gesetzt sind (beide Modi).
    if (form.planned_start && form.planned_end && form.planned_end.isBefore(form.planned_start, 'day')) {
      notify(t('project.datesOrder', 'Das geplante Ende darf nicht vor dem geplanten Start liegen'), 'warning');
      return;
    }

    const payload: Record<string, unknown> = {
      name: form.name,
      nickname: form.nickname.trim() || null,
      description: form.description || undefined,
      customer_id: form.customer_id || undefined,
      customer_cost_centre_id: form.customer_cost_centre_id || undefined,
      project_type_id: form.project_type_id || undefined,
      priority: form.priority,
      state: form.state,
      bill_type: form.bill_type,
      planned_start: form.planned_start?.format('YYYY-MM-DD') ?? null,
      planned_end: form.planned_end?.format('YYYY-MM-DD') ?? null,
      deadline: form.deadline?.format('YYYY-MM-DD') ?? null,
      project_no: form.project_no || undefined,
      project_reference: form.project_reference || undefined,
      link_uri: form.link_uri || undefined,
    };

    try {
      if (mode === 'edit' && initialData?.id) {
        await updateMutation.mutateAsync({ id: initialData.id as string, data: payload });
        notify(tc('status.saved', 'Gespeichert'), 'success');
        onClose();
        return;
      }
      const result = await createMutation.mutateAsync(payload) as { data?: { id: string } };
      const projectId = result?.data?.id;
      // Projektleitung als Freitext-Teammitglied anlegen (entkoppelt von Coworkern).
      const leitung = form.projektleitung.trim();
      let leadFailed = false;
      if (projectId && leitung) {
        try {
          await apiClient.post(`/api/v1/projects/${projectId}/team`, { name: leitung, rolle: 'Projektleiter' });
        } catch (partErr) {
          const partMsg = partErr instanceof ApiError ? partErr.message : tc('errors.generic', 'Fehler');
          notify(
            t('participants.leadAssignFailed', 'Projekt angelegt — Projektleitung konnte nicht angelegt werden: {{msg}}', { msg: partMsg }),
            'warning',
          );
          leadFailed = true;
        }
      }
      if (!leadFailed) {
        notify(tc('status.saved', 'Gespeichert'), 'success');
      }
      onClose();
      // Beide Erfolgsfaelle (mit/ohne Projektleitungs-Warnung) springen ins neue Projekt.
      if (projectId) {
        navigate(`/projects/${projectId}`);
      }
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : tc('errors.generic', 'Fehler');
      // Eine Abweisung wegen fehlender Berechtigung (403) ist kein Programmfehler —
      // sie wird als Hinweis gemeldet, damit sie nicht als Stoerung gelesen wird.
      const severity = err instanceof ApiError && err.status === 403 ? 'warning' : 'error';
      notify(msg, severity);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const updateField = <K extends keyof ProjectFormData>(key: K, value: ProjectFormData[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
    <Dialog open={open} onClose={() => !isSaving && onClose()} maxWidth="sm" fullWidth>
      <AppDialogTitle>
        {mode === 'edit'
          ? t('dialog.editTitle', 'Projekt bearbeiten')
          : t('dialog.createTitle', 'Neues Projekt erstellen')}
      </AppDialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField
            fullWidth
            required
            label={t('fields.name', 'Name')}
            value={form.name}
            onChange={(e) => updateField('name', e.target.value)}
          />

          <TextField
            fullWidth
            label={t('fields.nickname', 'Projekt-Spitzname')}
            value={form.nickname}
            onChange={(e) => updateField('nickname', e.target.value)}
            inputProps={{ maxLength: 120 }}
            helperText={t('fields.nicknameHelp', 'Kurzlabel für die Timeline-Übersicht (optional).')}
          />

          {mode === 'create' && (
            <TextField
              fullWidth
              required
              label={t('participants.leadLabel', 'Projektleitung')}
              placeholder={t('participants.leadPlaceholder', 'Name der Projektleitung')}
              value={form.projektleitung}
              onChange={(e) => updateField('projektleitung', e.target.value)}
              error={!form.projektleitung.trim()}
              helperText={t('participants.leadHelp', 'Wird als Projektleiter ins Team aufgenommen.')}
            />
          )}

          <Autocomplete
            options={customers}
            getOptionLabel={(opt) => opt.name}
            value={customers.find((c) => c.id === form.customer_id) ?? null}
            onChange={(_, selected) => {
              updateField('customer_id', selected?.id ?? '');
              updateField('customer_cost_centre_id', '');
            }}
            filterOptions={(options, { inputValue }) => {
              const lower = inputValue.toLowerCase();
              return options.filter((c) => c.name.toLowerCase().includes(lower));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('project.customer', 'Kunde')}
                placeholder={t('project.searchCustomer', 'Kunde suchen...')}
              />
            )}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
          />

          {form.customer_id && costCenters.length > 0 && (
            <FormControl fullWidth>
              <InputLabel>{t('project.costCentre', 'Kostenstelle')}</InputLabel>
              <Select
                value={form.customer_cost_centre_id}
                label={t('project.costCentre', 'Kostenstelle')}
                onChange={(e) => updateField('customer_cost_centre_id', e.target.value)}
              >
                <MenuItem value="">{tc('actions.noSelection', '— Keine —')}</MenuItem>
                {costCenters.map((cc) => (
                  <MenuItem key={cc.id} value={cc.id}>{cc.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('fields.description', 'Beschreibung')}
            value={form.description}
            onChange={(e) => updateField('description', e.target.value)}
          />

          <FormControl fullWidth>
            <InputLabel>{t('fields.projectType', 'Projekttyp')}</InputLabel>
            <Select
              value={form.project_type_id}
              label={t('fields.projectType', 'Projekttyp')}
              onChange={(e) => updateField('project_type_id', e.target.value)}
            >
              <MenuItem value="">{tc('actions.noSelection', '— Keine Auswahl —')}</MenuItem>
              {projectTypes.map((pt) => (
                <MenuItem key={pt.id} value={pt.id}>{pt.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>{t('fields.priority', 'Priorität')}</InputLabel>
            <Select
              value={form.priority}
              label={t('fields.priority', 'Priorität')}
              onChange={(e) => updateField('priority', e.target.value)}
            >
              {PRIORITIES.map((p) => (
                <MenuItem key={p} value={p}>{t(`priorities.${p}`, p)}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>{t('fields.state', 'Status')}</InputLabel>
            <Select
              value={form.state}
              label={t('fields.state', 'Status')}
              onChange={(e) => updateField('state', e.target.value)}
            >
              {STATES.map((s) => (
                <MenuItem key={s} value={s}>{t(`states.${s}`, s)}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel>{t('fields.billType', 'Abrechnungsart')}</FormLabel>
            <RadioGroup
              row
              value={form.bill_type}
              onChange={(e) => updateField('bill_type', e.target.value)}
            >
              <FormControlLabel value="BILLABLE" control={<Radio />} label={t('billTypes.BILLABLE', 'Abrechenbar')} />
              <FormControlLabel value="BLANKET" control={<Radio />} label={t('billTypes.BLANKET', 'Pauschal')} />
            </RadioGroup>
          </FormControl>

          <DatePicker
            label={t('fields.plannedStart', 'Geplanter Start')}
            value={form.planned_start}
            onChange={(v) => updateField('planned_start', v)}
            slotProps={{
              textField: {
                fullWidth: true,
                required: mode === 'create',
                error: mode === 'create' && !form.planned_start,
              },
            }}
          />

          <DatePicker
            label={t('fields.plannedEnd', 'Geplantes Ende')}
            value={form.planned_end}
            // Deadline beim Anlegen aus dem geplanten Ende vorbelegen (nur wenn noch leer) — überschreibbar.
            onChange={(v) => setForm((f) => ({ ...f, planned_end: v, deadline: mode === 'create' && !f.deadline ? v : f.deadline }))}
            slotProps={{
              textField: {
                fullWidth: true,
                required: mode === 'create',
                error: mode === 'create' && !form.planned_end,
              },
            }}
          />

          <DatePicker
            label={t('fields.deadline', 'Deadline')}
            value={form.deadline}
            onChange={(v) => updateField('deadline', v)}
            slotProps={{ textField: { fullWidth: true } }}
          />

          <TextField
            fullWidth
            label={t('fields.projectNo', 'Projekt-Nr.')}
            value={form.project_no}
            onChange={(e) => updateField('project_no', e.target.value)}
          />

          <TextField
            fullWidth
            label={t('fields.projectReference', 'Referenz')}
            value={form.project_reference}
            onChange={(e) => updateField('project_reference', e.target.value)}
          />

          <TextField
            fullWidth
            label={t('fields.linkUri', 'Link')}
            value={form.link_uri}
            onChange={(e) => updateField('link_uri', e.target.value)}
            placeholder="https://..."
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          {tc('actions.cancel', 'Abbrechen')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={
            isSaving ||
            !form.name.trim() ||
            (mode === 'create' && (!form.projektleitung.trim() || !form.planned_start || !form.planned_end))
          }
          startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
        >
          {tc('actions.save', 'Speichern')}
        </Button>
      </DialogActions>
    </Dialog>
    </LocalizationProvider>
  );
}
